import { useState, type CSSProperties, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { resetPassword } from '../api/client'
import { useLocale } from '../i18n/context'

/**
 * Public password-reset apply page (ADR-061 §4). Reached from a hand-delivered
 * reset link (`/reset-password?token=…`); no session required and it is not
 * linked from any navigation. Sets a new password via the public endpoint, then
 * directs the user to sign in normally — the reset issues no session, so there
 * is nothing to auto-sign-in with here.
 */
export default function ResetPasswordPage() {
  const { tr } = useLocale()
  const t = tr.resetPassword
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (saving) return
    setError(null)

    // Client-side guards mirror the backend: length-only (ADR-060 S-5) + match.
    if (next.length < 8) {
      setError(t.errTooShort)
      return
    }
    if (next !== confirm) {
      setError(t.errMismatch)
      return
    }

    setSaving(true)
    try {
      await resetPassword(token, next)
      setDone(true)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      // 400 RESET_TOKEN_INVALID is the single response for expired / used /
      // unknown / malformed tokens — one message, never distinguishing which.
      // Anything else (network, 5xx, rate-limit) reads as a transient failure.
      setError(status === 400 ? t.errInvalid : t.errGeneric)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ fontWeight: 700, fontSize: 24, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: 6 }}>
            {'CardSmith' /* i18n-exempt: brand name */}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {done ? t.successTitle : t.title}
          </p>
        </div>

        {done ? (
          // ── Success: no session issued — hand off to normal sign-in ──
          <>
            <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5, marginBottom: 24 }}>
              {t.successBody}
            </p>
            <Link to="/login" style={{ ...primaryButtonStyle, display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              {t.signIn}
            </Link>
          </>
        ) : !token ? (
          // ── Missing token: the link is incomplete, no form to show ──
          <>
            <div role="alert" style={errorBoxStyle}>{t.errMissing}</div>
            <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14 }}>
              <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                {t.signIn}
              </Link>
            </p>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5, marginBottom: 20 }}>
              {t.desc}
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Hidden username field is intentionally omitted — a locked-out
                  user's identity is carried by the token, not typed here. */}
              <div>
                <label style={labelStyle}>{t.newPassword}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>{t.confirm}</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={inputStyle}
                />
              </div>

              {error && (
                <div role="alert" style={errorBoxStyle}>{error}</div>
              )}

              <button
                type="submit"
                disabled={saving}
                style={{
                  ...primaryButtonStyle,
                  opacity: saving ? 0.65 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  marginTop: 4,
                }}
              >
                {saving ? t.saving : t.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

const pageStyle: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: 'var(--bg-page)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
}

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 400,
  backgroundColor: 'var(--bg-surface)',
  borderRadius: 'var(--radius-md)',
  padding: 40,
  boxShadow: 'var(--shadow-float)',
  border: '1px solid var(--border)',
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
}

const inputStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  backgroundColor: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '12px 16px',
  color: 'var(--text-primary)',
  fontSize: 15,
  outline: 'none',
  transition: 'border-color 0.15s',
  fontFamily: 'var(--font-ui)',
}

const primaryButtonStyle: CSSProperties = {
  backgroundColor: 'var(--accent-fill)',
  color: 'var(--text-on-accent)',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '13px 24px',
  fontSize: 15,
  fontWeight: 600,
  width: '100%',
  fontFamily: 'var(--font-ui)',
  transition: 'background-color 0.15s, transform 0.15s',
}

const errorBoxStyle: CSSProperties = {
  backgroundColor: 'var(--accent-soft)',
  border: '1px solid var(--danger)',
  borderRadius: 'var(--radius-sm)',
  padding: '10px 14px',
  color: 'var(--danger)',
  fontSize: 14,
  lineHeight: 1.4,
}
